import { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  message,
  Popconfirm,
  Card,
  Space,
  Row,
  Col,
  Upload,
  Image,
  Select,
  Progress,
  List,
  Divider
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  InboxOutlined,
  CloseOutlined
} from '@ant-design/icons';
import { photoAPI, albumAPI } from '../utils/api';
import { useAuth } from '../hooks/useAuthHook';
import useDocumentTitle from '../hooks/useDocumentTitle';
import { useParams } from 'react-router-dom';
import { validateTextField, validateDescription } from '../utils/validator';
import { IMG_URL } from '../utils/api';

const PhotoManagement = () => {
  const { user } = useAuth();
  const { username } = useParams();
  const [photos, setPhotos] = useState([]);
  const [albums, setAlbums] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingPhoto, setEditingPhoto] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadFiles, setUploadFiles] = useState([]);
  const [form] = Form.useForm();

  // 从URL参数获取ID：上传场景是相册ID，编辑场景是照片ID
  const queryParams = new URLSearchParams(window.location.search);
  const idParam = queryParams.get('id');
  const isUploadPage = window.location.pathname.includes('/album/upload/');

  // 根据是上传还是编辑动态设置网页标题
  const pageTitle = isUploadPage ? '上传照片' : (idParam ? '编辑照片' : '照片管理');
  useDocumentTitle(pageTitle, {
    prefix: '',
    suffix: ' - rtycn'
  });

  useEffect(() => {
    fetchAlbums();
    if (idParam) {
      if (isUploadPage) {
        // 上传场景：id 是相册ID，预选相册并加载其照片列表
        const albumId = Number(idParam);
        form.setFieldsValue({ albumId });
        fetchPhotos(albumId);
      } else {
        // 编辑场景：id 是照片ID
        fetchPhotoById(idParam);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idParam]);

  const fetchPhotos = async (albumId) => {
    if (!albumId) {
      setPhotos([]);
      return;
    }
    try {
      setLoading(true);
      const response = await photoAPI.getPhotosByAlbum(albumId);
      setPhotos(response.data || []);
    } catch (error) {
      console.error('获取照片列表失败:', error);
      message.error('获取照片列表失败，请刷新页面重试');
    } finally {
      setLoading(false);
    }
  };

  const fetchAlbums = async () => {
    try {
      const response = await albumAPI.getAlbumsByAuthor(user?.username || '');
      setAlbums(response.data || []);
    } catch (error) {
      console.error('获取相册列表失败:', error);
      message.error('获取相册列表失败，请刷新页面重试');
    }
  };

  const fetchPhotoById = async (id) => {
    try {
      const response = await photoAPI.getPhoto(id);
      const photoData = response.data;
      if (!photoData) {
        message.warning('未找到该照片，可能已被删除');
        return;
      }
      setEditingPhoto(photoData);
      form.setFieldsValue({
        name: photoData.title,
        description: photoData.description,
        albumId: photoData.albumId
      });
      setModalVisible(true);
      fetchPhotos(photoData.albumId);
    } catch (error) {
      console.error('获取照片详情失败:', error);
      message.error('获取照片详情失败，请刷新页面重试');
    }
  };

  const handleUpload = async () => {
    const albumId = form.getFieldValue('albumId');

    if (!albumId) {
      message.error('请先选择一个相册');
      return;
    }

    if (!uploadFiles || uploadFiles.length === 0) {
      message.error('请先选择要上传的图片文件');
      return;
    }

    // 验证每个文件的信息
    for (const file of uploadFiles) {
      if (file.name) {
        const nameResult = validateTextField(file.name, { fieldName: '照片名称', max: 200 });
        if (!nameResult.valid) {
          message.error(`"${file.name}" - ${nameResult.message}`);
          return;
        }
      }

      if (file.description) {
        const descResult = validateDescription(file.description, { fieldName: '照片描述', max: 500 });
        if (!descResult.valid) {
          message.error(`"${file.name}" - ${descResult.message}`);
          return;
        }
      }
    }

    try {
      setUploading(true);
      setUploadProgress(0);

      const uploadPromises = uploadFiles.map(async (file, index) => {
        const formData = new FormData();
        formData.append('file', file.originFileObj || file);
        formData.append('albumId', albumId);
        formData.append('title', file.name || file.originFileObj?.name || '');
        formData.append('description', file.description || '');
        formData.append('authorId', user?.username || '');
        formData.append('authorName', user?.nickname || user?.username || '');

        const response = await photoAPI.uploadPhoto(formData);
        setUploadProgress(Math.round(((index + 1) / uploadFiles.length) * 100));
        return response;
      });

      await Promise.all(uploadPromises);

      message.success(`成功上传 ${uploadFiles.length} 张照片`);
      form.resetFields();
      setUploadFiles([]);
      setModalVisible(false);
      if (albumId) {
        fetchPhotos(albumId);
      }
    } catch (error) {
      console.error('上传照片失败:', error);
      const errorMsg = error?.response?.data?.message || error?.message || '照片上传失败，请稍后重试';
      message.error(errorMsg);
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleEdit = (record) => {
    setEditingPhoto(record);
    form.setFieldsValue({
      name: record.title || record.name,
      description: record.description,
      albumId: record.albumId
    });
    setModalVisible(true);
  };

  const handleDelete = async (photoId, albumId) => {
    try {
      await photoAPI.deletePhoto(photoId);
      message.success('照片删除成功');
      if (albumId) {
        fetchPhotos(albumId);
      }
    } catch (error) {
      console.error('删除照片失败:', error);
      message.error('删除照片失败，请稍后重试');
    }
  };

  const handleUpdate = async (values) => {
    try {
      // 前端验证
      if (values.name) {
        const nameResult = validateTextField(values.name, { required: true, fieldName: '照片名称', max: 200 });
        if (!nameResult.valid) {
          message.error(nameResult.message);
          return;
        }
      }

      if (values.description) {
        const descResult = validateDescription(values.description, { fieldName: '照片描述', max: 500 });
        if (!descResult.valid) {
          message.error(descResult.message);
          return;
        }
      }

      const updateData = {
        ...values,
        title: values.name
      };
      delete updateData.name;
      await photoAPI.updatePhoto(editingPhoto.id, updateData);
      message.success('更新成功');
      setModalVisible(false);
      if (editingPhoto.albumId) {
        fetchPhotos(editingPhoto.albumId);
      }
    } catch (error) {
      console.error('更新照片失败:', error);
      message.error('更新照片失败，请稍后重试');
    }
  };

  const uploadProps = {
    name: 'files',
    multiple: true,
    showUploadList: false,
    accept: 'image/*',
    beforeUpload: (file, fileList) => {
      const fileNameWithoutExt = file.name.replace(/\.[^/.]+$/, '');
      const newFile = {
        uid: file.uid,
        name: fileNameWithoutExt,
        originFileObj: file,
        description: '',
        url: URL.createObjectURL(file)
      };
      setUploadFiles(prev => [...prev, newFile]);
      return false;
    },
    onDrop: (e) => {
      const files = Array.from(e.dataTransfer.files).filter(file => file.type.startsWith('image/'));
      const newFiles = files.map(file => ({
        uid: `${Date.now()}-${Math.random()}`,
        name: file.name.replace(/\.[^/.]+$/, ''),
        originFileObj: file,
        description: '',
        url: URL.createObjectURL(file)
      }));
      setUploadFiles(prev => [...prev, ...newFiles]);
    }
  };

  const handleRemoveFile = (uid) => {
    setUploadFiles(prev => {
      const file = prev.find(f => f.uid === uid);
      if (file?.url) {
        URL.revokeObjectURL(file.url);
      }
      return prev.filter(f => f.uid !== uid);
    });
  };

  const handleUpdateFileInfo = (uid, field, value) => {
    setUploadFiles(prev => prev.map(f => 
      f.uid === uid ? { ...f, [field]: value } : f
    ));
  };

  const columns = [
    {
      title: '缩略图',
      dataIndex: 'imageUrl',
      key: 'imageUrl',
      width: 100,
      render: (imageUrl) => {
        // 如果imageUrl是相对路径，拼接IMG_URL前缀
        const fullImageUrl = imageUrl?.startsWith('http') ? imageUrl : `${IMG_URL}${imageUrl}`;
        return (
          <Image
            width={60}
            height={60}
            src={fullImageUrl}
            style={{ objectFit: 'cover', borderRadius: 4 }}
            preview={{
              mask: <EyeOutlined />,
            }}
          />
        );
      },
    },
    {
      title: '照片名称',
      dataIndex: 'title',
      key: 'title',
      render: (title, record) => title || record.name,
    },
    {
      title: '描述',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
    },
    {
      title: '所属相册',
      dataIndex: 'albumId',
      key: 'albumId',
      render: (albumId) => {
        const album = albums.find(a => a.id === albumId);
        return album ? album.title || album.name : '未知相册';
      },
    },
    {
      title: '上传时间',
      dataIndex: 'createTime',
      key: 'createTime',
      render: (time) => new Date(time).toLocaleString(),
    },
    {
      title: '操作',
      key: 'action',
      width: 200,
      render: (_, record) => (
        <Space size="small">
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            编辑
          </Button>
          <Popconfirm
            title="确定删除这张照片吗？"
            onConfirm={() => handleDelete(record.id, record.albumId)}
            okText="确定"
            cancelText="取消"
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ width: '100%', maxWidth: 800, margin: '0 auto' }}>
      <Card
        title="照片管理"
        extra={
          <Button 
            type="primary" 
            icon={<PlusOutlined />} 
            onClick={() => {
              setEditingPhoto(null);
              form.resetFields();
              setUploadFiles([]);
              setModalVisible(true);
            }}
          >
            上传照片
          </Button>
        }
      >
        <Table
          columns={columns}
          dataSource={photos}
          loading={loading}
          rowKey="id"
          pagination={{
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `共 ${total} 张照片`,
          }}
        />
      </Card>

      <Modal
        title={editingPhoto ? '编辑照片信息' : '上传照片'}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          setUploadFiles([]);
        }}
        footer={null}
        width={800}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={editingPhoto ? handleUpdate : null}
        >
          <Form.Item
            name="albumId"
            label="选择相册"
            rules={[{ required: true, message: '请选择相册' }]}
          >
            <Select placeholder="请选择相册" onChange={(value) => fetchPhotos(value)}>
              {albums.map(album => (
                <Select.Option key={album.id} value={album.id}>
                  {album.title || album.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="name"
            label="照片名称"
            rules={[{ required: true, message: '请输入照片名称' }]}
          >
            <Input placeholder="请输入照片名称" />
          </Form.Item>

          <Form.Item
            name="description"
            label="照片描述"
          >
            <Input.TextArea 
              placeholder="请输入照片描述" 
              rows={4}
            />
          </Form.Item>

          {!editingPhoto && (
            <>
              <Form.Item
                label="上传照片"
                required
              >
                <Upload {...uploadProps} style={{ width: '100%' }}>
                  <div style={{ 
                    padding: '24px', 
                    border: '2px dashed #d9d9d9', 
                    borderRadius: '8px',
                    textAlign: 'center',
                    cursor: 'pointer',
                    background: '#fafafa'
                  }}>
                    <p style={{ fontSize: '48px', color: '#d9d9d9', marginBottom: '8px' }}>
                      <InboxOutlined />
                    </p>
                    <p style={{ color: '#1890ff', fontSize: '16px' }}>
                      点击或拖拽文件到此处上传
                    </p>
                    <p style={{ color: '#999', fontSize: '14px' }}>
                      支持jpg、png、gif等图片格式，可同时上传多张图片
                    </p>
                  </div>
                </Upload>
              </Form.Item>

              {uploadFiles.length > 0 && (
                <Form.Item label="待上传图片列表">
                  <List
                    dataSource={uploadFiles}
                    renderItem={(file, index) => (
                      <List.Item
                        style={{ 
                          padding: '16px',
                          marginBottom: '8px',
                          border: '1px solid #f0f0f0',
                          borderRadius: '8px',
                          background: '#fff'
                        }}
                      >
                        <List.Item.Meta
                          avatar={
                            <div style={{ position: 'relative' }}>
                              <Image
                                width={80}
                                height={80}
                                src={file.url}
                                style={{ objectFit: 'cover', borderRadius: '4px' }}
                              />
                              <span style={{
                                position: 'absolute',
                                top: '-8px',
                                left: '-8px',
                                background: '#1890ff',
                                color: '#fff',
                                width: '20px',
                                height: '20px',
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '12px'
                              }}>
                                {index + 1}
                              </span>
                            </div>
                          }
                          description={
                            <div style={{ width: '100%' }}>
                              <Row gutter={[16, 16]}>
                                <Col span={24}>
                                  <Input
                                    placeholder="照片名称"
                                    value={file.name}
                                    onChange={(e) => handleUpdateFileInfo(file.uid, 'name', e.target.value)}
                                    prefix={<span style={{ color: '#999' }}>名称:</span>}
                                  />
                                </Col>
                                <Col span={24}>
                                  <Input.TextArea
                                    placeholder="照片描述"
                                    value={file.description}
                                    onChange={(e) => handleUpdateFileInfo(file.uid, 'description', e.target.value)}
                                    rows={2}
                                  />
                                </Col>
                              </Row>
                            </div>
                          }
                        />
                        <Button
                          type="text"
                          danger
                          icon={<CloseOutlined />}
                          onClick={() => handleRemoveFile(file.uid)}
                        >
                          移除
                        </Button>
                      </List.Item>
                    )}
                  />
                </Form.Item>
              )}

              {uploading && uploadProgress > 0 && (
                <Form.Item>
                  <Progress percent={uploadProgress} status="active" />
                </Form.Item>
              )}
            </>
          )}

          <Form.Item>
            <Row gutter={16}>
              <Col span={12}>
                <Button onClick={() => setModalVisible(false)}>
                  取消
                </Button>
              </Col>
              <Col span={12} style={{ textAlign: 'right' }}>
                {editingPhoto ? (
                  <Button type="primary" htmlType="submit">
                    更新
                  </Button>
                ) : (
                  <Button
                    type="primary"
                    loading={uploading}
                    onClick={handleUpload}
                    disabled={uploadFiles.length === 0}
                  >
                    开始上传 ({uploadFiles.length})
                  </Button>
                )}
              </Col>
            </Row>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default PhotoManagement;